import React, { useEffect, useState } from "react";
import { Select, DatePicker, Space, message } from "antd";
import dayjs from "dayjs";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";
import AttendanceInsightsView from "../../EmployeeDashboard/TeamAttendance/AttendanceInsightsView";

const { RangePicker } = DatePicker;

// HR / company-wide AI attendance insights. The admin endpoint is
// department-scoped (departmentId is required - it keeps large company-wide
// data out of the AI service), so HR picks one department + a date range.
const AdminAttendanceInsights = () => {
  const [departments, setDepartments] = useState([]);
  const [departmentId, setDepartmentId] = useState(null);
  const [range, setRange] = useState([dayjs().subtract(6, "day"), dayjs()]);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get(API.department.getList);
        const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setDepartments(list);
      } catch (e) {
        message.error("Failed to load departments");
      }
    })();
  }, []);

  const load = async (deptId, r) => {
    if (!deptId || !r || !r[0] || !r[1]) return;
    try {
      setLoading(true);
      const res = await axiosInstance.get(
        API.attendance.adminAttendanceInsights(
          deptId,
          r[0].format("YYYY-MM-DD"),
          r[1].format("YYYY-MM-DD")
        )
      );
      setInsights(res.data || null);
    } catch (e) {
      console.error("Failed to load admin attendance insights", e);
      message.error("Failed to load attendance insights");
      setInsights(null);
    } finally {
      setLoading(false);
    }
  };

  const departmentOptions = [
    // "All Departments" sends NO departmentId so the backend runs the company-wide
    // insight (all employees together) for cross-department comparison. Listed
    // first per Sai (Jun-21). Needs Mudassir's all-company route.
    { value: "all", label: "All Departments" },
    ...(Array.isArray(departments) ? departments : []).map((d) => ({
      value: d.id ?? d.departmentId,
      label: d.name ?? d.departmentName,
    })),
    // Synthetic "Executives" option: backend filters by Executive designation
    // when departmentId is -1 (Mudassir Jun-21). -1 (not 0) because 0 is falsy
    // and would be read as "no selection" by the dropdown's checks.
    { value: -1, label: "Executives" },
  ];

  return (
    <>
      <Space wrap style={{ marginBottom: 16 }}>
        <Select
          placeholder="Select Department"
          style={{ width: 220 }}
          value={departmentId}
          onChange={(v) => {
            setDepartmentId(v);
            load(v, range);
          }}
          options={departmentOptions}
        />
        <RangePicker
          value={range}
          allowClear={false}
          disabledDate={(d) => d && d.isAfter(dayjs(), "day")}
          onChange={(r) => {
            if (r && r[0] && r[1]) {
              setRange(r);
              load(departmentId, r);
            }
          }}
        />
      </Space>

      <AttendanceInsightsView
        insights={insights}
        loading={loading}
        emptyText="Select a department and date range to view insights"
      />
    </>
  );
};

export default AdminAttendanceInsights;
